import { useEffect, useCallback } from 'react'
import { __ } from '@wordpress/i18n'

/**
 * Custom hook for EDD download selection validation
 *
 * Provides shared validation logic for EDD download/category/tag selection,
 * including zero reviews warning detection.
 *
 * @param {Object} params
 * @param {Array} params.selectedDownloads - Currently selected downloads
 * @param {Array} params.selectedCategories - Currently selected categories
 * @param {Array} params.selectedTags - Currently selected tags
 * @param {Function} params.setValidationError - State setter for validation error
 * @param {boolean} params.enabled - Whether to enable automatic validation (default: true)
 * @returns {Object} Validation utilities
 */
const useEddValidation = ( {
    selectedDownloads = [],
    selectedCategories = [],
    selectedTags = [],
    setValidationError,
    enabled = true
} ) => {

    /**
     * Check if at least one item is selected (download, category, or tag)
     */
    const hasAnySelection = useCallback( () => {
        return selectedDownloads.length > 0 || selectedCategories.length > 0 || selectedTags.length > 0;
    }, [ selectedDownloads, selectedCategories, selectedTags ] );

    /**
     * Calculate total reviews across all selections
     */
    const getTotalSelectedReviews = useCallback( () => {
        const downloadReviews = selectedDownloads.reduce( ( sum, download ) => sum + ( download.review_count || 0 ), 0 );
        const categoryReviews = selectedCategories.reduce( ( sum, cat ) => sum + ( cat.review_count || 0 ), 0 );
        const tagReviews = selectedTags.reduce( ( sum, tag ) => sum + ( tag.review_count || 0 ), 0 );
        return downloadReviews + categoryReviews + tagReviews;
    }, [ selectedDownloads, selectedCategories, selectedTags ] );

    /**
     * Validate selection and return error message if invalid
     * @returns {string|null} Error message or null if valid
     */
    const validateSelection = useCallback( () => {
        if ( ! hasAnySelection() ) {
            return __( 'You need to at least select 1 download', 'sb-customizer' );
        }
        return null;
    }, [ hasAnySelection ] );

    /**
     * Get zero reviews warning message if applicable
     * @returns {string|null} Warning message or null
     */
    const getZeroReviewsWarning = useCallback( () => {
        if ( hasAnySelection() && getTotalSelectedReviews() === 0 ) {
            return __( 'Your selection has no reviews.', 'sb-customizer' );
        }
        return null;
    }, [ hasAnySelection, getTotalSelectedReviews ] );

    // Auto-update validation error when selection changes (zero reviews warning)
    useEffect( () => {
        if ( ! setValidationError || ! enabled ) return;

        const hasSelection = hasAnySelection();
        const totalReviews = getTotalSelectedReviews();

        if ( hasSelection && totalReviews === 0 ) {
            setValidationError( __( 'Your selection has no reviews.', 'sb-customizer' ) );
        } else if ( hasSelection && totalReviews > 0 ) {
            // Clear warning when reviews are available
            setValidationError( null );
        }
    }, [ selectedDownloads, selectedCategories, selectedTags, hasAnySelection, getTotalSelectedReviews, setValidationError, enabled ] );

    return {
        hasAnySelection,
        getTotalSelectedReviews,
        validateSelection,
        getZeroReviewsWarning
    };
};

export default useEddValidation;
